import React, { useState, useEffect } from 'react'
import {
  EditIcon,
  TrashIcon,
  FileTextIcon,
  FilterIcon,
  XIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'

interface ProposalRequest {
  id: string
  request_number?: string
  proposal_number?: string
  title: string
  description?: string
  project_type?: string
  property_address?: string
  property_city?: string
  property_state?: string
  property_zip?: string
  property_type?: string
  square_footage?: number
  estimated_budget?: number
  preferred_start_date?: string
  preferred_completion_date?: string
  urgency_level?: string
  special_requirements?: string
  accessibility_needs?: string
  existing_contractor?: boolean
  contractor_details?: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  company_name?: string
  lead_id?: string
  customer_id?: string
  property_id?: string
  status: string
  assigned_to?: string
  review_notes?: string
  internal_notes?: string
  attachments?: Array<{
    name: string
    url: string
    type: string
    size: number
    uploaded_at: string
  }>
  blueprint_links?: Array<{
    title: string
    url: string
    description: string
  }>
  submitted_at?: string
  reviewed_at?: string
  quoted_at?: string
  converted_at?: string
  created_at: string
  updated_at?: string
  customer?: {
    id: string
    company_name?: string
    contact_first_name?: string
    contact_last_name?: string
  }
  lead?: {
    id: string
    company_name?: string
    contact_first_name?: string
    contact_last_name?: string
  }
}

interface ProposalRequestsListProps {
  onViewRequest: (request: ProposalRequest) => void
  onEditRequest: (request: ProposalRequest) => void
  onDeleteRequest: (id: string) => void
  onCreateProposal?: (request: ProposalRequest) => void
  refreshTrigger?: number // When this changes, refresh the list
}

export function ProposalRequestsList({
  onViewRequest,
  onEditRequest,
  onDeleteRequest,
  onCreateProposal,
  refreshTrigger,
}: ProposalRequestsListProps) {
  const [requests, setRequests] = useState<ProposalRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('open')
  const [showCustomFilters, setShowCustomFilters] = useState(false)
  const [customFilters, setCustomFilters] = useState<Array<{
    id: string
    field: string
    operator: string
    value: string
  }>>([])

  useEffect(() => {
    fetchRequests()
  }, [refreshTrigger]) // Refresh when refreshTrigger changes

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('record_type', 'request')
        .order('created_at', { ascending: false })

      if (error) throw error

      if (!data || data.length === 0) {
        setRequests([])
        setLoading(false)
        return
      }

      // Enrich requests with customer/lead data (similar to proposals)
      const enrichedRequests = await Promise.all(
        data.map(async (request) => {
          let customer = null
          let lead = null

          // Fetch customer if exists
          if (request.customer_id) {
            const { data: customerData } = await supabase
              .from('customers')
              .select('id, company_name, contact_first_name, contact_last_name')
              .eq('id', request.customer_id)
              .single()
            customer = customerData
          }

          // Fetch lead if exists
          if (request.lead_id) {
            const { data: leadData } = await supabase
              .from('leads')
              .select('id, company_name, contact_first_name, contact_last_name')
              .eq('id', request.lead_id)
              .single()
            lead = leadData
          }

          return {
            ...request,
            customer,
            lead,
          }
        })
      )

      setRequests(enrichedRequests)
    } catch (error) {
      console.error('Error fetching proposal requests:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'bg-blue-100 text-blue-800'
      case 'reviewing':
        return 'bg-yellow-100 text-yellow-800'
      case 'quoted':
        return 'bg-purple-100 text-purple-800'
      case 'converted':
        return 'bg-green-100 text-green-800'
      case 'declined':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }


  const filteredRequests = requests.filter((request) => {
    const searchLower = searchTerm.toLowerCase()
    const customerName = request.customer
      ? (request.customer.company_name ||
          `${request.customer.contact_first_name || ''} ${
            request.customer.contact_last_name || ''
          }`.trim() ||
          '').toLowerCase()
      : request.lead
      ? (request.lead.company_name ||
          `${request.lead.contact_first_name || ''} ${
            request.lead.contact_last_name || ''
          }`.trim() ||
          '').toLowerCase()
      : ''

    const matchesSearch =
      (request.title || '').toLowerCase().includes(searchLower) ||
      (request.contact_name || '').toLowerCase().includes(searchLower) ||
      (request.property_address || '').toLowerCase().includes(searchLower) ||
      (request.request_number || request.proposal_number || '').toLowerCase().includes(searchLower) ||
      customerName.includes(searchLower)

    let matchesStatus = true
    if (filterStatus === 'all') {
      matchesStatus = true
    } else if (filterStatus === 'open') {
      // Open includes: submitted, reviewing, quoted
      matchesStatus = ['submitted', 'reviewing', 'quoted'].includes(request.status)
    } else if (filterStatus === 'closed') {
      // Closed includes: converted, declined, rejected
      matchesStatus = ['converted', 'declined', 'rejected'].includes(request.status)
    } else {
      // Individual status match
      matchesStatus = request.status === filterStatus
    }

    // Apply custom filters
    const matchesCustomFilters = customFilters.every((filter) => {
      const fieldValue = (request as any)[filter.field]
      if (fieldValue === null || fieldValue === undefined) return false

      const filterValue = filter.value.toLowerCase()
      const requestValue = String(fieldValue).toLowerCase()

      switch (filter.operator) {
        case 'contains':
          return requestValue.includes(filterValue)
        case 'equals':
          return requestValue === filterValue
        case 'starts_with':
          return requestValue.startsWith(filterValue)
        case 'greater_than':
          return Number(fieldValue) > Number(filter.value)
        case 'less_than':
          return Number(fieldValue) < Number(filter.value)
        default:
          return true
      }
    })

    return matchesSearch && matchesStatus && matchesCustomFilters
  })

  const addCustomFilter = () => {
    setCustomFilters([
      ...customFilters,
      {
        id: `filter-${Date.now()}`,
        field: 'contact_name',
        operator: 'contains',
        value: '',
      },
    ])
    setShowCustomFilters(true)
  }

  const removeCustomFilter = (id: string) => {
    setCustomFilters(customFilters.filter((f) => f.id !== id))
  }

  const updateCustomFilter = (id: string, field: string, value: string) => {
    setCustomFilters(
      customFilters.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    )
  }

  const availableFilterFields = [
    { value: 'contact_name', label: 'Contact Name' },
    { value: 'contact_email', label: 'Contact Email' },
    { value: 'company_name', label: 'Company Name' },
    { value: 'property_address', label: 'Property Address' },
    { value: 'property_city', label: 'Property City' },
    { value: 'property_state', label: 'Property State' },
    { value: 'property_type', label: 'Property Type' },
    { value: 'project_type', label: 'Project Type' },
    { value: 'urgency_level', label: 'Urgency Level' },
    { value: 'estimated_budget', label: 'Estimated Budget' },
    { value: 'square_footage', label: 'Square Footage' },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search requests..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="submitted">Submitted</option>
              <option value="reviewing">Reviewing</option>
              <option value="quoted">Quoted</option>
              <option value="converted">Converted</option>
              <option value="declined">Declined</option>
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={addCustomFilter}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <FilterIcon className="w-4 h-4" />
              Add Filter
            </button>
            <div className="text-sm text-gray-500 flex-1 text-right">
              {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Custom Filters */}
        {customFilters.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Custom Filters</h3>
              <button
                onClick={() => {
                  setCustomFilters([])
                  setShowCustomFilters(false)
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear All
              </button>
            </div>
            <div className="space-y-2">
              {customFilters.map((filter) => (
                <div key={filter.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <select
                    value={filter.field}
                    onChange={(e) => updateCustomFilter(filter.id, 'field', e.target.value)}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availableFilterFields.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filter.operator}
                    onChange={(e) => updateCustomFilter(filter.id, 'operator', e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="contains">Contains</option>
                    <option value="equals">Equals</option>
                    <option value="starts_with">Starts With</option>
                    {(filter.field === 'estimated_budget' || filter.field === 'square_footage') && (
                      <>
                        <option value="greater_than">Greater Than</option>
                        <option value="less_than">Less Than</option>
                      </>
                    )}
                  </select>
                  <input
                    type="text"
                    value={filter.value}
                    onChange={(e) => updateCustomFilter(filter.id, 'value', e.target.value)}
                    placeholder="Value..."
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={() => removeCustomFilter(filter.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remove filter"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Requests List - Table Format */}
      {filteredRequests.length === 0 ? (
        <div className="text-center py-12 px-6">
          <p className="text-gray-500">
            {searchTerm || filterStatus !== 'all'
              ? 'No proposal requests match your search criteria.'
              : 'No proposal requests found. Get started by creating your first request.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="w-full">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 min-w-0">
                      <div>
                        <button
                          onClick={() => onViewRequest(request)}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left"
                        >
                          {request.title || 'Untitled Request'}
                        </button>
                        <div className="text-sm text-gray-500 truncate">
                          {request.request_number || request.proposal_number || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {request.customer
                          ? request.customer.company_name ||
                            `${request.customer.contact_first_name || ''} ${
                              request.customer.contact_last_name || ''
                            }`.trim() ||
                            'N/A'
                          : request.lead
                          ? request.lead.company_name ||
                            `${request.lead.contact_first_name || ''} ${
                              request.lead.contact_last_name || ''
                            }`.trim() ||
                            'N/A'
                          : request.contact_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                      {request.estimated_budget
                        ? `$${request.estimated_budget.toLocaleString()}`
                        : '$0'}
                    </td>
                    <td className="px-3 py-3 min-w-0">
                      <select
                        value={request.status}
                        onChange={(e) => {
                          // Update status in database
                          supabase
                            .from('proposals')
                            .update({ status: e.target.value })
                            .eq('id', request.id)
                            .then(() => {
                              // Refresh the list
                              fetchRequests()
                            })
                        }}
                        className={`text-xs font-semibold rounded-full px-2 py-1 border-0 focus:ring-2 focus:ring-blue-500 cursor-pointer ${getStatusColor(
                          request.status
                        )}`}
                      >
                        <option value="submitted">Submitted</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="quoted">Quoted</option>
                        <option value="converted">Converted</option>
                        <option value="declined">Declined</option>
                      </select>
                    </td>
                    <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                      {request.created_at || request.submitted_at
                        ? new Date(request.created_at || request.submitted_at || '').toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-3 py-3 min-w-0">
                      <div className="flex items-center gap-2">
                        {onCreateProposal && request.status !== 'converted' && (
                          <button
                            onClick={() => onCreateProposal(request)}
                            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-colors"
                            title="Create Proposal from Request"
                          >
                            <FileTextIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => onEditRequest(request)}
                          className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-md transition-colors"
                          title="Edit Request"
                        >
                          <EditIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDeleteRequest(request.id)}
                          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete Request"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
